import React from "react";
import { Platform, Text, View, Image, TouchableOpacity, Dimensions } from "react-native";
import { Tabs, useRouter } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { StackActions } from "@react-navigation/native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

type IoniconName = React.ComponentProps<typeof Ionicons>["name"];

// Tap a tab → always land on that tab's list screen, popping any nested detail
// (dog profile, followers) off its stack. We focus the tab, then dispatch
// popToTop DIRECTLY at the nested stack via its key. This works on web AND
// native — unlike CommonActions.reset (no-ops on web → user trapped on the
// detail screen) or navigate({screen:'index'}) (PUSHES a duplicate list on top,
// leaving a phantom back button that returns to the detail screen).
function resetTabToRoot(navigation: any, tabName: string) {
  const tabRoute = navigation.getState().routes.find((r: any) => r.name === tabName);
  navigation.navigate(tabName);
  if (tabRoute?.state?.key) {
    navigation.dispatch({ ...StackActions.popToTop(), target: tabRoute.state.key });
  }
}

function TabIcon({ name, color, size }: { name: IoniconName; color: string; size: number }) {
  return <Ionicons name={name} size={size} color={color} />;
}

function LogoSmall() {
  if (Platform.OS === "web") {
    return <img src="/logo-main.png" alt="Dash-Hound" style={{ height: 32, objectFit: "contain" } as any} />;
  }
  return <Image source={require("../../assets/images/logo-main.png")} style={{ height: 32, width: 120 }} resizeMode="contain" />;
}

function HeaderWithTitle({ title, rightElement }: { title: string; rightElement?: React.ReactNode }) {
  const screenWidth = Dimensions.get("window").width;
  return (
    <View style={{ flexDirection: "row", alignItems: "center", width: screenWidth - 32, paddingHorizontal: 0 }}>
      <View style={{ width: (screenWidth - 32) / 3, alignItems: "flex-start" }}>
        <LogoSmall />
      </View>
      <View style={{ width: (screenWidth - 32) / 3, alignItems: "center" }}>
        <Text style={{ fontSize: 18, fontWeight: "700", color: "#1B2A4A" }}>{title}</Text>
      </View>
      <View style={{ width: (screenWidth - 32) / 3, alignItems: "flex-end" }}>
        {rightElement || null}
      </View>
    </View>
  );
}

function MascotBar() {
  return (
    <View style={{ alignItems: "center", paddingVertical: 5, backgroundColor: "#F4F6F8" }}>
      {Platform.OS === "web" ? (
        <img src="/mascot.png" alt="" style={{ height: 35, objectFit: "contain", opacity: 0.3 } as any} />
      ) : (
        <Image source={require("../../assets/images/mascot.png")} style={{ height: 35, width: 90, opacity: 0.3 }} resizeMode="contain" />
      )}
    </View>
  );
}

function DashboardAddButton() {
  const router = useRouter();
  return (
    <TouchableOpacity
      onPress={() => router.push("/(tabs)/dogs/add" as any)}
      style={{
        backgroundColor: "#1B2A4A",
        flexDirection: "row",
        alignItems: "center",
        paddingHorizontal: 12,
        paddingVertical: 7,
        borderRadius: 8,
        gap: 4,
        zIndex: 10,
      }}
    >
      <Ionicons name="add" size={14} color="#FFFFFF" />
      <Text style={{ color: "#FFFFFF", fontWeight: "600", fontSize: 12 }}>Add Dog</Text>
    </TouchableOpacity>
  );
}

export default function TabLayout() {
  const insets = useSafeAreaInsets();
  const androidBottomInset = Platform.OS === "android" ? insets.bottom : 0;
  const tabBarHeight =
    Platform.OS === "ios" ? 85 : Platform.OS === "web" ? 80 : 60 + androidBottomInset;
  const tabBarPaddingBottom =
    Platform.OS === "ios" ? 25 : Platform.OS === "web" ? 22 : 8 + androidBottomInset;

  return (
    <View style={{ flex: 1 }}>
      <Tabs
        screenOptions={{
          tabBarActiveTintColor: "#1B2A4A",
          tabBarInactiveTintColor: "#9ca3af",
          tabBarShowIcon: true,
          tabBarIconStyle: { display: "flex" },
          tabBarStyle: {
            backgroundColor: "#ffffff",
            borderTopColor: "#e5e7eb",
            borderTopWidth: 1,
            height: tabBarHeight,
            paddingBottom: tabBarPaddingBottom,
            paddingTop: 8,
          },
          headerStyle: { backgroundColor: "#ffffff" },
          headerShadowVisible: false,
        }}
      >
        <Tabs.Screen
          name="index"
          options={{
            title: "Dashboard",
            headerTitle: () => <HeaderWithTitle title="Dashboard" rightElement={<DashboardAddButton />} />,
            tabBarIcon: ({ color, size }) => <TabIcon name="home" color={color} size={size} />,
          }}
        />
        <Tabs.Screen
          name="dogs"
          options={{
            title: "Dogs",
            tabBarIcon: ({ color, size }) => <TabIcon name="paw" color={color} size={size} />,
            headerShown: false,
          }}
          listeners={({ navigation }) => ({
            tabPress: (e) => {
              e.preventDefault();
              resetTabToRoot(navigation, "dogs");
            },
          })}
        />
        <Tabs.Screen
          name="events"
          options={{
            title: "Events",
            tabBarIcon: ({ color, size }) => <TabIcon name="calendar" color={color} size={size} />,
            headerShown: false,
          }}
          listeners={({ navigation }) => ({
            tabPress: (e) => {
              e.preventDefault();
              resetTabToRoot(navigation, "events");
            },
          })}
        />
        <Tabs.Screen
          name="rankings"
          options={{
            title: "Rankings",
            tabBarIcon: ({ color, size }) => <TabIcon name="trophy" color={color} size={size} />,
            headerShown: false,
          }}
        />
        <Tabs.Screen
          name="settings"
          options={{
            title: "Settings",
            headerTitle: () => <HeaderWithTitle title="Settings" />,
            tabBarIcon: ({ color, size }) => <TabIcon name="settings-sharp" color={color} size={size} />,
          }}
        />
      </Tabs>
      <View style={{ position: "absolute", bottom: tabBarHeight, left: 0, right: 0, pointerEvents: "none" } as any}>
        <MascotBar />
      </View>
    </View>
  );
}
